import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface DemoCursorProps {
  targetId?: string;
  visible: boolean;
}

export function DemoCursor({ targetId, visible }: DemoCursorProps) {
  const [pos, setPos] = useState({ x: -120, y: -120 });
  const [isClicking, setIsClicking] = useState(false);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Place cursor at a reasonable starting position after mount
  useEffect(() => {
    setPos({ x: window.innerWidth * 0.62, y: window.innerHeight * 0.28 });
  }, []);

  useEffect(() => {
    if (!targetId || !visible) return;

    const move = () => {
      const el = document.querySelector(`[data-demo-id="${targetId}"]`);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      setPos({ x: rect.left + rect.width * 0.28, y: rect.top + rect.height * 0.32 });
      return true;
    };

    if (!move()) {
      const t = setTimeout(move, 350);
      return () => clearTimeout(t);
    }

    // Simulate click ~1.4s after cursor starts moving
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      setIsClicking(true);
      setTimeout(() => setIsClicking(false), 320);
    }, 1400);

    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, [targetId, visible]);

  return (
    <motion.div
      animate={{ x: pos.x, y: pos.y, opacity: visible ? 1 : 0 }}
      transition={{
        x: { duration: 1.15, ease: [0.22, 1, 0.36, 1] },
        y: { duration: 1.15, ease: [0.22, 1, 0.36, 1] },
        opacity: { duration: 0.35 },
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      <motion.div
        animate={{ scale: isClicking ? 0.82 : 1 }}
        transition={{ duration: 0.13, ease: 'easeOut' }}
      >
        <svg
          width="28"
          height="32"
          viewBox="0 0 28 32"
          fill="none"
          shapeRendering="geometricPrecision"
        >
          <defs>
            <filter id="demo-cursor-shadow" x="-25%" y="-15%" width="150%" height="150%">
              <feDropShadow dx="0.5" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.52)" />
            </filter>
          </defs>
          <path
            d="M5 2 L5 23 L9.8 18.4 L13.1 27.2 L16.3 25.9 L13 17.2 L21.5 17.2 Z"
            fill="white"
            stroke="#111827"
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
            filter="url(#demo-cursor-shadow)"
          />
        </svg>
      </motion.div>

      {isClicking && (
        <motion.div
          initial={{ scale: 0, opacity: 0.7 }}
          animate={{ scale: 4.5, opacity: 0 }}
          transition={{ duration: 0.52, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#3994ff',
            pointerEvents: 'none',
          }}
        />
      )}
    </motion.div>
  );
}
